import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { ComponentProps } from "react";

import {
  botaoContornoClass,
  botaoDeVidroClass,
  botaoPrimarioClass,
  controleEmPilulaClass,
} from "../../lib/superficies";
import { cn } from "../../lib/utils";

/**
 * TUT-563a (Lumen 2.0) — o botão de vidro. Decisões do dono de 29/10
 * (`docs/product/plano-tut-563-lumen-2.md`, "O desenho", item 2):
 *
 * - seis PAPÉIS (`primario`, `vidro`, `contorno`, `discreto`, `perigo`,
 *   `link`) e o `filtro` da barra, que continua em pílula;
 * - três TAMANHOS: `sm` 32 px e 12 px de letra, `md` 40 e 13, `lg` 44 e 14;
 * - peso 550 (o Inter variável carrega o eixo inteiro), canto de 12 px
 *   (`--radius-botao`).
 *
 * TUT-563b — o codemod (`tools/codemod-lumen-2`) trocou os nomes antigos
 * pelos papéis, e os apelidos de PAPEL (`default`, `accent`, `quiet`,
 * `secondary`, `outline`, `ghost`, `destructive`) saíram do tipo: escrevê-los
 * não compila mais.
 */

export const PAPEIS_DO_BOTAO = ["primario", "vidro", "contorno", "discreto", "perigo", "link", "filtro"] as const;
export type PapelDoBotao = (typeof PAPEIS_DO_BOTAO)[number];

export const TAMANHOS_DO_BOTAO = ["sm", "md", "lg"] as const;
export type TamanhoDoBotao = (typeof TAMANHOS_DO_BOTAO)[number];

/**
 * O DESFOQUE do vidro (decisão 24): fora de tabela e lista o botão desfoca,
 * como na prévia; debaixo de um contêiner `data-sem-desfoque` (o `DataTable`,
 * o cartão de quadro, as listas da casa) ele fica sem, por desempenho. Quem
 * decide é o primitivo; nenhuma tela escreve desfoque (P34,
 * `acabamento-leve.test.ts`).
 */
const desfoqueDoVidro = "[backdrop-filter:var(--vidro-desfoque)] [[data-sem-desfoque]_&]:[backdrop-filter:none]";

// TUT-472 — alternância (`aria-pressed`) mostra qual está ligada.
const ligadoClass =
  "aria-pressed:border-primary aria-pressed:bg-[var(--primary-highlight)] aria-pressed:text-primary";

/** As classes de cada papel: o `IconButton` veste as mesmas (uma receita só). */
export const CLASSES_DO_PAPEL: Readonly<Record<PapelDoBotao, string>> = {
  primario: botaoPrimarioClass,
  vidro: `${botaoDeVidroClass} ${desfoqueDoVidro} ${ligadoClass}`,
  contorno: `${botaoContornoClass} ${ligadoClass}`,
  discreto: "border-transparent bg-transparent text-foreground shadow-none hover:bg-[var(--hover)]",
  perigo:
    "border-[color:color-mix(in_srgb,var(--destructive)_45%,transparent)] bg-transparent text-destructive shadow-none hover:bg-[color-mix(in_srgb,var(--destructive)_12%,transparent)] focus-visible:ring-destructive/25",
  link: "h-auto border-0 bg-transparent p-0 text-link underline-offset-4 shadow-none hover:underline",
  // TUT-510 (revisão 1) — o FILTRO da barra (Lumen): a pílula sobre o vidro
  // forte, irmã da busca. TUT-511 — ligado (`aria-pressed`), se preenche com
  // o par do botão primário, como a opção do alternador.
  filtro:
    controleEmPilulaClass +
    " text-foreground shadow-none hover:border-primary hover:bg-[var(--hover)] aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-pressed:hover:bg-primary",
};

const CLASSES_DO_TAMANHO: Readonly<Record<TamanhoDoBotao, string>> = {
  sm: "min-h-[var(--control-h-compact)] gap-1.5 px-3 py-1 text-legenda has-[>svg]:px-2.5 [&_svg:not([class*='size-'])]:size-[0.9375rem]",
  md: "min-h-[var(--control-h)] px-4 py-2 text-corpo has-[>svg]:px-3",
  lg: "min-h-[var(--control-h-lg)] px-5 py-2 text-destaque has-[>svg]:px-4",
};

const buttonVariants = cva(
  // TUT-302 — retorno tátil ao clicar (decisão do dono, 23/09): só o Button,
  // não linhas/cartões. `motion-reduce` desliga o encolhimento.
  "button inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-botao)] border border-transparent font-botao outline-none transition-[background,color,border-color,box-shadow,transform,filter] duration-[var(--dur-fast)] ease-[var(--ease-out)] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/25 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        ...CLASSES_DO_PAPEL,
      },
      size: {
        ...CLASSES_DO_TAMANHO,
      },
    },
    compoundVariants: [
      // TUT-511 — o filtro é pílula em QUALQUER tamanho.
      { variant: "filtro", class: "rounded-full" },
      // A âncora LEGADA que a `style-snapshot.spec.ts` ainda lê, sem efeito de
      // estilo. TUT-563b — a `button-quiet` e a `button-secondary` saíram com
      // os apelidos: quem distinguia os dois lê o `data-variant`.
      { variant: "primario", class: "button-primary" },
    ],
    defaultVariants: {
      variant: "primario",
      size: "md",
    },
  },
);

export type ButtonProps = ComponentProps<"button"> & {
  variant?: PapelDoBotao | null | undefined;
  size?: TamanhoDoBotao | null | undefined;
  asChild?: boolean;
};

/** O papel do botão (sem `variant`, o `primario`). */
export function papelDoBotao(variant: ButtonProps["variant"]): PapelDoBotao {
  return variant ?? "primario";
}

/**
 * O tamanho do botão (sem `size`, o `md`). TUT-563b — os apelidos (`row`, `xs`,
 * `default`) e os tamanhos de ícone (`icon*`, agora o `IconButton`) saíram.
 */
export function tamanhoDoBotao(size: ButtonProps["size"]): TamanhoDoBotao {
  return size ?? "md";
}

export function Button({ className, variant = "primario", size = "md", asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={papelDoBotao(variant)}
      data-size={tamanhoDoBotao(size)}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { buttonVariants };
